<template>
    <div class="forum-category-preview">

        <router-link
            :to="categoryUrl"
            class="category-header">

            <div class="category-meta">
                <h2>
                    {{ category.label }}
                </h2>
                <div
                    class="summary"
                    v-if="category.summary">
                    | {{ category.summary }}
                </div>
                <div class="thread-count">
                    <span v-if="threadCount !== null">
                        {{ formattedThreadCount }}
                    </span>
                </div>
            </div>

            <div class="img-wrapper">
                <img
                    :src="category.bannerUrl"
                    v-if="category.bannerUrl">
                <img
                    src="../../../assets/img/ttto-thumbnail.jpg"
                    v-else>
            </div>
        </router-link>

        <div class="category-body">
            <ForumThreadList
                :sortable="true"
                :sortOrder="sortOrder"
                :threads="threads"
                @update:sort-order="sortOrder = $event">
            </ForumThreadList>

            <div class="category-link">
                <router-link :to="categoryUrl">
                    View All
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
    // ForumCategoryPreview: displays the category banner followed by a short
    // list of recent threads in that category. Used on the forum home page.

    import ForumService from 'src/services/forum';
    import ForumThreadList from './thread-list';

    export default {
        props: {
            category: {
                type: Object, // ForumCategory (see forum service)
                required: true,
            },
        },

        components: {
            ForumThreadList,
        },

        data() {
            return {
                threads: [],
                threadCount: null,
                sortOrder: 'default',

                loadingPromise: null,
                reloadAfter: false,
            };
        },

        computed: {
            categoryUrl() {
                return {
                    name: 'forum-category',
                    params: {
                        categoryId: this.category.id,
                        categorySlug: this.category.name,
                    },
                };
            },

            formattedThreadCount() {
                if (this.threadCount === 1) {
                    return `1 Thread`;
                } else {
                    return `${this.threadCount} Threads`;
                }
            },
        },

        methods: {
            async reloadThreads() {
                if (this.loadingPromise) {
                    this.reloadAfter = true;
                    return await this.loadingPromise;
                }

                const load = async () => {
                    while (true) {
                        const res = await ForumService.getThreadsAndCount({
                            categoryId: this.category.id,
                            order: this.sortOrder,
                            limit: 5,
                        });
                        this.threads = res.threads;
                        this.threadCount = res.count;

                        if (this.reloadAfter) {
                            this.reloadAfter = false;
                        } else {
                            break;
                        }
                    }
                };

                try {
                    this.reloadAfter = false;
                    this.loadingPromise = load();
                    await this.loadingPromise;

                } finally {
                    this.loadingPromise = null;
                }
            },
        },

        watch: {
            'category.id': {
                handler: function() {
                    this.reloadThreads();
                },
                immediate: true,
            },

            sortOrder: function() {
                this.reloadThreads();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .category-header {
        display: block;
        background: #333;

        &:hover {
            text-decoration: none;

            h2, .thread-count {
                color: #33ccff;
            }

            .img-wrapper img {
                transform: scale(1.05);
                opacity: 0.8;
            }
        }

        .category-meta {
            padding: 20px;
        }

        h2 {
            display: inline;
            font-size: 28px;
            margin: 0;
        }

        .summary {
            color: #888;
            display: inline;
            font-size: 22px;
        }

        .thread-count {
            margin-top: 5px;
            color: #b0b0b0;

            // Keep full line height even when empty (before count has loaded)
            &:empty::before {
                content: '';
                display: inline-block;
            }
        }

        .img-wrapper {
            height: 200px;
            overflow: hidden;

            img {
                position: relative;
                width: 100%;
                height: inherit;
                object-fit: cover;
                background: #333;
                opacity: 1;
                transition-property: transform, opacity;
                transition-duration: 0.3s;
            }
        }
    }

    .category-body {
        margin: 10px 0;

        .category-link {
            padding: 15px 10px;
            border-bottom: 1px solid #333;
        }
    }
</style>



// WEBPACK FOOTER //
// category-preview.vue?b52a6720